import { z } from 'zod';
import type { auctions_type, heroes_type, users } from '@prisma/client';

export type ISession = Pick<users, 'id' | 'uid' | 'username' | 'email' | 'role' | 'email_verified_at' | 'banned_at' | 'accepted_tos'>;
export type IAuctionType = auctions_type;

export const CreateAuctionSchema = z.object({
  title: z.string().max(100),
  content: z.string().nullable().optional(),
  tos: z.string().nullable().optional(),
  image: z.string().nullable().optional(),
  status: z.enum(['upcoming', 'ongoing', 'ended']).optional().default('upcoming'),
  published: z.coerce.boolean().optional().default(false),
  address: z.string().nullable().optional(),
  address_ref: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  region_id: z.coerce.number().nullable(),
  map_url: z.string().nullable().optional(),
  start_date: z.coerce.date().nullable(),
  end_date: z.coerce.date().nullable(),
  use_lots_date: z.coerce.boolean().optional().default(false),
  live_date: z.coerce.date().nullable(),
  embed_html_video: z.string().nullable().optional(),
  commission_percentage: z.number().optional().default(10),
  type: z.enum(['face_to_face', 'live', 'timed', 'live_with_preoffers']).optional().default('timed'),
  subscription_type_id: z.coerce.number().nullable(),
  awarded_notification_channel: z.enum(['sms', 'email']).optional().default("email"),
  outbided_notification_channel: z.enum(['sms', 'email']).nullable().optional().default(null),
  outbided_default_sms_email_trigger_minutes: z.coerce.number().optional().default(20),
  deleted_at: z.coerce.date().nullable(),
});

export const CreateBidSchema = z.object({
  lot_uid: z.string(),
  net_amount: z.coerce.bigint().min(0n)
});

export const UpdateBidSchema = z.object({
  awarded: z.coerce.boolean().optional(),
});

const HERO_TYPES: heroes_type[] = [
  'title_text_1',
  'title_text_image_1',
  'title_text_image_right_1',
  'title_text_image_left_1',
  'title_text_image_right_2',
  'title_text_image_left_2',
  'title_text_image_right_3',
  'title_text_image_bottom_1',
] as const;

export const CreateHeroSchema = z.object({
  internal_description: z.string(),
  type: z.enum(HERO_TYPES as any),
  title: z.string().nullable(),
  text: z.string().nullable(),
  image: z.string().nullable(),
  published: z.coerce.boolean(),
});

export const CreateHeroButtonSchema = z.object({
  label: z.string(),
  href: z.string(),
  tenant_id: z.union([z.number(), z.bigint()]),
});

export const CreateManyHeroButtonsSchema = z.array(CreateHeroButtonSchema);

export const CreateLiveAuctionSchema = z.object({
  auction_id: z.string().min(3).max(32),
  html_embed: z.string().nullable(),
  current_lot_id: z.string().nullable(),
  close_lot_at: z.coerce.date().nullable(),
  published: z.coerce.boolean(),
  status: z.enum(['open', 'closed']).optional().default('closed'),
});

export const CreateLotSchema = z.object({
  vendor_id: z.coerce.bigint(),
  nr: z.string().max(100),
  description: z.string(),
  images: z.array(z.string()).nullable().default(null),
  tax_percentage: z.coerce.number().min(0).max(100).default(10),
  commission_percentage: z.coerce.number().min(0).max(100).default(10),
  base_price: z.coerce.bigint().min(0n),
  bid_increment: z.coerce.bigint().min(0n),
  start_date: z.coerce.date(),
  end_date: z.coerce.date(),
  featured: z.coerce.boolean(),
  published: z.coerce.boolean(),
  status: z.enum(['upcoming', 'ongoing', 'ended']).default('upcoming')
});

export const SigninEmailSchema = z.object({
  email: z.string().email('El email no es válido'),
});

export const SigninSchema = z.object({
  email: z.string().email('El email no es válido'),
  password: z.string({ required_error: 'La contraseña es requerida' })
});

export const SignupSchema = z.object({
  username: z.string()
    .min(3, 'El nombre del usuario debe tener al menos 3 caracteres')
    .max(50, 'El nombre del usuario no puede ser mayor a 50 caracteres'),
  firstname: z.string()
    .min(3, 'El nombre debe tener al menos 3 caracteres')
    .max(50, 'El nombreno puede ser mayor a 50 caracteres'),
  lastname: z.string()
    .min(3, 'El apellido debe tener al menos 3 caracteres')
    .max(50, 'El apellido no puede ser mayor a 50 caracteres'),
  document_number: z.string()
    .min(6, 'El numero de documento debe tener al menos 6 dígitos')
    .max(8, 'El numero de documento no puede ser mayor a 8 dígitos')
    .refine((value) => {
      const regex = /^[0-9]+$/;
      return regex.test(value);
    }, 'El numero de documento solo puede contener números'),
  phone: z.string()
    .refine((value) => value.length === 9, 'El teléfono debe tener 9 dígitos')
    .refine((value) => {
      const regex = /^[0-9]+$/;
      return regex.test(value);
    }, 'El teléfono solo puede contener números')
    .refine((value) => {
      const regex = /^(96|97|98|99)[0-9]+$/;
      return regex.test(value);
    }, 'El teléfono debe comenzar con 96x, 97x, 98x o 99x'),
  address: z.string().min(10, 'La dirección debe tener al menos 10 caracteres'),
  city: z.string().min(3, 'La ciudad debe tener al menos 10 caracteres').max(32),
  region_id: z.coerce.bigint({ required_error: 'El departamento es requerido' }),
  email: z.string().email('El email no es válido'),
  password: z.string()
    .min(8, 'La contraseña debe tener almenos 8 caracteres')
    .max(32, 'La contraseña no puede ser mayor a 32 caracteres'),
  accepted_tos: z.coerce.boolean({ required_error: 'Debes aceptar los términos y condiciones' })
    .refine((value) => value === true, 'Debes aceptar los términos y condiciones')
});

export const UpdatePasswordSchema = z.object({
  password: z.string().min(8).max(32).optional(),
  new_password: z.string()
    .min(8, 'La contraseña debe tener almenos 8 caracteres')
    .max(32, 'La contraseña no puede ser mayor a 32 caracteres'),
  confirm_new_password: z.string().min(8).max(32)
});

export const UpdateProfileSchema = z.object({
  username: z.string()
    .min(3, 'El nombre del usuario debe tener al menos 3 caracteres')
    .max(50, 'El nombre del usuario no puede ser mayor a 50 caracteres')
    .optional(),
  firstname: z.string()
    .min(3, 'El nombre debe tener al menos 3 caracteres')
    .max(50, 'El nombreno puede ser mayor a 50 caracteres')
    .optional(),
  lastname: z.string()
    .min(3, 'El apellido debe tener al menos 3 caracteres')
    .max(50, 'El apellido no puede ser mayor a 50 caracteres')
    .optional(),
  document_number: z.string()
    .min(6, 'El numero de documento debe tener al menos 6 dígitos')
    .max(8, 'El numero de documento no puede ser mayor a 8 dígitos')
    .refine((value) => {
      const regex = /^[0-9]+$/;
      return regex.test(value);
    }, 'El numero de documento solo puede contener números').optional(),
  phone: z.string()
    .refine((value) => value.length === 9, 'El teléfono debe tener 9 dígitos')
    .refine((value) => {
      const regex = /^[0-9]+$/;
      return regex.test(value);
    }, 'El teléfono solo puede contener números')
    .refine((value) => {
      const regex = /^(96|97|98|99)[0-9]+$/;
      return regex.test(value);
    }, 'El teléfono debe comenzar con 96x, 97x, 98x o 99x').optional(),
  address: z.string().min(10, 'La dirección debe tener al menos 10 caracteres').optional(),
  city: z.string().min(3, 'La ciudad debe tener al menos 10 caracteres').max(32).optional(),
  region_id: z.coerce.number({ required_error: 'El departamento es requerido' }).optional(),
});

export type ICloudflareImageEndpoint = {
  uploadURL: string;
  id: string;
};

export const VendorSchema = z.object({
  name: z.string().min(3).max(100),
  business_name: z.string().min(3).max(100),
  vat_id: z.string(),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  region_id: z.coerce.number(),
});
